import { Checkbox, CheckboxGroupProps, Form, Input, Row } from 'admin-web-ui'
import React from 'react'
import styled from 'styled-components'
import { Project } from '../../../api'
import { CustomerGroup } from '../../../auth'
import { ProjectGroup, getGroupName } from 'admin-web-utils'
import { useConsoleContext } from '../../../containers/Console'

const UserNameContainer = styled.div`
  width: 100%;
  display: flex;
`

const StyledFormItem = styled(Form.Item)`
  flex-grow: 1;
  &:nth-child(1) {
    margin-right: 8px;
  }
  &:nth-child(2) {
    margin-left: 8px;
  }
`

const ProjectSectionLegend = styled.legend`
  /* Specificity for global styles */
  &#project-access-legend {
    margin-bottom: 16px;
  }
`

const ProjectSection = styled.div`
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.greys.loblolly};
  margin-bottom: 16px;

  & .header,
  & .body {
    padding: 24px;
  }

  & .header {
    border-bottom: solid 1px ${({ theme }) => theme.greys.loblolly};
    font-weight: bold;
  }

  & .ant-form-item {
    margin-bottom: 0;
  }
`

const StyledRow = styled(Row)`
  margin: 0 0 16px;
  padding-left: 8px;
  &:last-child {
    margin: 0;
  }
`

const RoleDescription = styled.span`
  color: ${({ theme }) => theme.greys.raven};
`

export type UserFormFieldData = {
  firstName: string
  lastName: string
  email: string
  customerGroups: string[]
} & Record<`projectGroups-${string}`, string[]>

export interface Props {
  projects: Project[]
  emailSubmitError?: string
  username?: string
}

export const UserFormFields: React.FC<Props> = (props) => {
  const { projects, emailSubmitError } = props
  const { me } = useConsoleContext()

  return (
    <>
      <fieldset>
        <legend>User Information</legend>
        <UserNameContainer>
          <StyledFormItem
            label="First name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter a first name' }]}
          >
            <Input placeholder="First Name" />
          </StyledFormItem>
          <StyledFormItem
            label="Last name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter a last name' }]}
          >
            <Input placeholder="Last Name" />
          </StyledFormItem>
        </UserNameContainer>
        <Form.Item
          label="Email"
          name="email"
          help={emailSubmitError}
          validateStatus={emailSubmitError && 'error'}
          rules={[
            {
              type: 'email',
              message: 'Please enter a valid email address',
            },
            { required: true, message: 'Please input your email address' },
          ]}
        >
          <Input type="email" placeholder="Email" disabled={!!props.username} />
        </Form.Item>
      </fieldset>

      <Form.Item name="customerGroups">
        <Checkbox.Group>
          <AccessGroupCheckbox
            userGroupName={CustomerGroup.AccessAdmin}
            label="Account Admin"
            description="Allows user to manage account users and project access"
            disabled={
              me.username === props.username &&
              me.authGroups.includes(CustomerGroup.AccessAdmin)
            }
          />
        </Checkbox.Group>
      </Form.Item>

      <fieldset>
        <ProjectSectionLegend id="project-access-legend">
          Project Access
        </ProjectSectionLegend>

        {projects.map((project) => (
          <Form.Item key={project.id} name={`projectGroups-${project.id}`}>
            <ProjectAccessGroups key={project.id} project={project} />
          </Form.Item>
        ))}
      </fieldset>
    </>
  )
}

/**
 * Custom form field for project group checkboxes.
 */
export const ProjectAccessGroups: React.FC<
  CheckboxGroupProps & { project: Project }
> = (props) => {
  const { project } = props
  const adminGroup = getGroupName(ProjectGroup.Admin, project.id)
  const currentGroups = props.value ?? []
  const isProjectAdmin = currentGroups.includes(adminGroup)

  return (
    <Checkbox.Group {...props}>
      <ProjectSection key={project.id}>
        <div className="header">{project.name}</div>
        <div className="body">
          <AccessGroupCheckbox
            userGroupName={getGroupName(ProjectGroup.Admin, project.id)}
            label="Administrator"
            description="Allows access to all features and capabilities"
          />
          <AccessGroupCheckbox
            userGroupName={getGroupName(ProjectGroup.Developer, project.id)}
            label="Developer"
            description="Allows access to development related features"
            disabled={isProjectAdmin}
          />
          <AccessGroupCheckbox
            userGroupName={getGroupName(ProjectGroup.Support, project.id)}
            label="Support"
            description="Allows access to support related features and capabilities"
            disabled={isProjectAdmin}
          />
          <AccessGroupCheckbox
            userGroupName={getGroupName(ProjectGroup.Finance, project.id)}
            label="Finance"
            description="Allows access to billing related features and capabilities"
            disabled={isProjectAdmin}
          />
          <AccessGroupCheckbox
            userGroupName={getGroupName(ProjectGroup.Marketing, project.id)}
            label="Marketing"
            description="Allows access to marketing related features and capabilities"
            disabled={isProjectAdmin}
          />
          <AccessGroupCheckbox
            userGroupName={getGroupName(ProjectGroup.L2Support, project.id)}
            label="L2 Support"
            description="Allows access to vault features and capabilities"
            disabled={isProjectAdmin}
          />
        </div>
      </ProjectSection>
    </Checkbox.Group>
  )
}

export const AccessGroupCheckbox: React.FC<{
  label: string
  description: string
  userGroupName: string
  disabled?: boolean
}> = (props) => {
  return (
    <StyledRow gutter={16}>
      <Checkbox disabled={props.disabled} value={props.userGroupName}>
        {props.label} <RoleDescription>{props.description}</RoleDescription>
      </Checkbox>
    </StyledRow>
  )
}
